import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronUp, ChevronDown, X, GripVertical } from "lucide-react";

// Standard "separation" styling — 1px brand-orange border + soft shadow,
// applied to every card slice of the pain-map widget so the AI Coach and
// Today tab render identically.
const FL_ORANGE = "#f47a20";
const CARD_BORDER = "1px solid rgba(244, 122, 32, 0.28)";
const CARD_SHADOW = "0 2px 12px -6px rgba(0, 0, 0, 0.45)";

/**
 * Anatomical body-map — uses the uploaded skeleton image as the literal
 * interactive background. The image contains BOTH anterior + posterior
 * views side-by-side, so the whole image is one coordinate system:
 *
 *   left half   →  0.0 ≤ x_pct < 0.5   →  view: "anterior"
 *   right half  →  0.5 ≤ x_pct ≤ 1.0   →  view: "posterior"
 *
 * We store click coordinates as PERCENTAGES of the image (`x_pct`,
 * `y_pct` ∈ [0, 1]) so the same marker renders identically on any screen
 * size and — critically — on the clinician read-only mirror.
 *
 * Backwards compatibility with older markers that stored absolute SVG
 * coords `x`, `y` (viewBox 0-240 × 0-480) is preserved via a fallback
 * inside `renderMark()`.
 *
 * `value` = [{ id, view, x_pct, y_pct, pain_before, pain_after, rpe,
 *   description, // legacy: x, y }]
 */

// The image is served from /public/assets and referenced by an absolute
// path so any React Router mount / basename doesn't break it.
const SKELETON_SRC = "/assets/skeleton-body-map.png";
const ZOOM_STEP = 0.15;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

// Resolve percentage coordinates for a marker — new markers use x_pct/y_pct,
// legacy ones (viewBox 240×480) get converted.
function toPct(m) {
  if (m.x_pct != null && m.y_pct != null) {
    return { xp: Number(m.x_pct), yp: Number(m.y_pct) };
  }
  // Legacy fallback — treat the previous viewBox as centered anterior only.
  return { xp: Math.min(0.5, (Number(m.x) || 120) / 480), yp: (Number(m.y) || 240) / 480 };
}

// ============================================================
// Interactive canvas — image + numbered markers overlay + zoom/pan
// ============================================================
function BodyCanvas({ marks, activeId, onOpen, onAdd, locked, testid,
  src = SKELETON_SRC, alt = "Skeleton — anterior and posterior views",
  resolveView = (xp) => (xp < 0.5 ? "anterior" : "posterior"),
  canPlace = () => true, overlay = null, onMove = null, imgStyle = null,
  maxImgHeight = null }) {
  const markerDrag = useRef(null);
  const downPos = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragState = useRef(null);   // { startX, startY, panX, panY } when panning
  // Two-finger pinch-to-zoom on touch devices. Tracks the initial finger
  // distance + zoom, then scales zoom by the current-to-initial ratio.
  const pinchState = useRef(null);  // { startDist, startZoom }
  // Live zoom/pan for the native listeners installed below. That effect only
  // re-runs when `locked` flips, so a plain closure would keep reading the
  // mount-time values.
  const view = useRef({ zoom: 1, pan: { x: 0, y: 0 } });
  useEffect(() => { view.current = { zoom, pan }; }, [zoom, pan]);

  // iter101.5 · Install native, non-passive listeners for wheel + touchmove.
  // React 18 attaches synthetic wheel/touch handlers as passive, so calling
  // e.preventDefault() inside them is a silent no-op — the browser still
  // page-scrolls (desktop) or pinch-zooms the whole page (mobile). Attaching
  // the same handlers natively with { passive: false } fixes both.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheelNative = (e) => {
      if (locked) return;
      e.preventDefault();
      const { zoom: z, pan: p } = view.current;
      const nz = clampZoom(z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
      if (nz === z) return;
      // Anchor the zoom on the pointer: the pixel under the cursor must stay
      // under the cursor. transformOrigin is "0 0", so pan has to absorb the
      // scale change — otherwise the map grows out of its top-left corner and
      // the joint the patient is aiming at slides away from the cursor.
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const k = nz / z;
      setPan(nz === MIN_ZOOM ? { x: 0, y: 0 } : { x: cx - (cx - p.x) * k, y: cy - (cy - p.y) * k });
      setZoom(nz);
    };
    const onTouchMoveNative = (e) => {
      // Prevent the browser from pinch-zooming the whole page when the user
      // is trying to pinch-zoom the body map. Two-finger only — leave single
      // touches alone so vertical scrolling elsewhere still works normally.
      if (e.touches && e.touches.length >= 2) e.preventDefault();
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    el.addEventListener("touchmove", onTouchMoveNative, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheelNative);
      el.removeEventListener("touchmove", onTouchMoveNative);
    };
  }, [locked]);

  // Distance between two touch points — used by pinch-to-zoom.
  const touchDist = (t0, t1) => {
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };
  const handleTouchStart = (e) => {
    if (locked) return;
    if (e.touches.length === 2) {
      pinchState.current = {
        startDist: touchDist(e.touches[0], e.touches[1]),
        startZoom: zoom,
      };
      dragState.current = null;  // pinch overrides pan
    }
  };
  const handleTouchMove = (e) => {
    if (!pinchState.current || e.touches.length < 2) return;
    const d = touchDist(e.touches[0], e.touches[1]);
    const nz = clampZoom(pinchState.current.startZoom * (d / (pinchState.current.startDist || 1)));
    const { zoom: z, pan: p } = view.current;
    if (nz === z) return;
    // Same anchoring as the wheel, around the midpoint of the two fingers.
    const rect = containerRef.current.getBoundingClientRect();
    const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
    const my = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
    const k = nz / z;
    setPan(nz === MIN_ZOOM ? { x: 0, y: 0 } : { x: mx - (mx - p.x) * k, y: my - (my - p.y) * k });
    setZoom(nz);
  };
  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) pinchState.current = null;
  };

  const handlePointerDown = (e) => {
    if (locked) return;
    if (pinchState.current) return;   // pinch takes precedence — skip pan
    downPos.current = { x: e.clientX, y: e.clientY, moved: false };
    // Middle-click, or Shift+Left, or any drag when zoomed → pan.
    if (zoom > 1.02 && (e.button === 0 || e.button === 1)) {
      dragState.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y, moved: false };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
  };
  const handlePointerMove = (e) => {
    if (downPos.current && Math.abs(e.clientX - downPos.current.x) + Math.abs(e.clientY - downPos.current.y) > 4) downPos.current.moved = true;
    if (markerDrag.current && onMove) {
      // Coordinates are read straight from the displayed image's box, so they
      // stay correct under any zoom/pan and any letterboxing (portrait images
      // that don't fill the container width).
      const rect = (imgRef.current || containerRef.current).getBoundingClientRect();
      const xp = (e.clientX - rect.left) / rect.width;
      const yp = (e.clientY - rect.top) / rect.height;
      markerDrag.current.moved = true;
      if (xp >= 0 && xp <= 1 && yp >= 0 && yp <= 1 && canPlace(xp, yp)) onMove(markerDrag.current.id, { x_pct: xp, y_pct: yp, view: resolveView(xp, yp) });
      return;
    }
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragState.current.moved = true;
    setPan({ x: dragState.current.panX + dx, y: dragState.current.panY + dy });
  };
  const handlePointerUp = (e) => {
    if (markerDrag.current) {
      const md = markerDrag.current; markerDrag.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      if (!md.moved) { downPos.current = null; onOpen(md.id); }
      return;
    }
    const moved = dragState.current?.moved;
    dragState.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (moved) return; // suppress the click that follows a pan-drag
    // Fall through to the container onClick.
  };

  const handleClick = (e) => {
    if (locked) return;
    if (e.target?.dataset?.marker) return;
    if (downPos.current?.moved) { downPos.current = null; return; }  // drag/pan must not add a marker
    // Coordinates relative to the displayed image box — accounts for zoom/pan
    // and for any empty space around a portrait image.
    const rect = (imgRef.current || containerRef.current).getBoundingClientRect();
    const xp = (e.clientX - rect.left) / rect.width;
    const yp = (e.clientY - rect.top) / rect.height;
    if (xp < 0 || xp > 1 || yp < 0 || yp > 1) return;
    if (!canPlace(xp, yp)) return;
    const view = resolveView(xp, yp);
    onAdd({ x_pct: xp, y_pct: yp, view });
  };

  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // Double-click / double-tap resets zoom + pan — replaces the removed
  // 1× reset button.
  const handleDoubleClick = (e) => {
    if (locked) return;
    e.stopPropagation();
    e.preventDefault();
    resetView();
  };

  return (
    <div className="relative">
      <div
        ref={containerRef}
        onDoubleClick={handleDoubleClick}
        title={locked ? "" : "Scroll to zoom · drag to pan · double-click to reset"}
        className={`relative w-full select-none overflow-hidden rounded-md ${maxImgHeight ? "flex items-center justify-center" : ""}`}
        style={{
          cursor: locked ? "default" : (zoom > 1.02 ? "grab" : "crosshair"),
          border: "1px solid var(--fl-border)",
          background: "#F3F4F6",
          touchAction: "none",
          ...(maxImgHeight ? { maxHeight: maxImgHeight } : {}),
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onClick={handleClick}
        data-testid={`${testid}-canvas`}
      >
        <div
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
            transition: dragState.current ? "none" : "transform 120ms ease-out",
            ...(maxImgHeight ? { display: "inline-block", lineHeight: 0 } : { width: "100%" }),
          }}
        >
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            className={maxImgHeight ? "block" : "w-full h-auto"}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            style={{
              userSelect: "none", pointerEvents: "none", display: "block",
              ...(maxImgHeight ? { maxHeight: maxImgHeight, maxWidth: "100%", width: "auto", height: "auto" } : {}),
              ...(imgStyle || {}),
            }}
          />
          {overlay}
          {marks.map((m) => {
            const { xp, yp } = toPct(m);
            return (
              <button
                key={m.id}
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!onMove) onOpen(m.id); }}
                onPointerDown={(e) => { if (onMove && !locked) { e.stopPropagation(); markerDrag.current = { id: m.id, moved: false }; e.currentTarget.closest("[data-testid$='-canvas']")?.setPointerCapture?.(e.pointerId); } }}
                data-marker="1"
                data-testid={`${testid}-marker-${m.number}`}
                className="absolute flex items-center justify-center rounded-full font-bold"
                style={{
                  left: `${xp * 100}%`,
                  top: `${yp * 100}%`,
                  transform: `translate(-50%, -50%) scale(${1 / zoom})`,
                  transformOrigin: "center",
                  width: activeId === m.id ? 22 : 20,
                  height: activeId === m.id ? 22 : 20,
                  fontSize: 11,
                  background: "var(--fl-orange)",
                  color: "#0A0A0A",
                  border: activeId === m.id ? "2px solid #fff" : "2px solid rgba(0,0,0,0.6)",
                  boxShadow: "0 4px 10px -3px rgba(0,0,0,0.4)",
                }}
              >
                {m.number}
              </button>
            );
          })}
        </div>
        {!locked && zoom > 1.02 && (
          <button
            type="button"
            data-marker="1"
            onClick={(e) => { e.stopPropagation(); resetView(); }}
            className="absolute top-2 right-2 z-10 text-[11px] px-2 py-1 rounded-md border"
            style={{ background: "rgba(0,0,0,0.7)", color: "#fff", borderColor: "rgba(255,255,255,0.3)" }}
            data-testid={`${testid}-reset`}
          >
            Reset / Fit
          </button>
        )}
      </div>
    </div>
  );
}

export { BodyCanvas };

// ============================================================
// Sidebar row — priority-list entry with the form fields
// ============================================================
function MarkerRow({
  mark, index, total, active, locked, onFocus, onUpdate, onRemove, onMoveUp, onMoveDown, testid,
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`fl-card p-3 ${active ? "ring-1" : ""}`}
      style={{
        borderColor: active
          ? "color-mix(in oklab, var(--fl-orange) 65%, transparent)"
          : "var(--fl-border)",
        background: active
          ? "color-mix(in oklab, var(--fl-orange) 6%, transparent)"
          : "transparent",
      }}
      onClick={() => onFocus(mark.id)}
      data-testid={`${testid}-row-${mark.number}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0"
          style={{ background: "var(--fl-orange)", color: "#0A0A0A" }}
        >
          {mark.number}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--fl-text)" }}>
            {ordinal(index + 1)} {t("bodymap.pain", "pain")}
          </div>
          <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
            {t(`bodymap.${mark.view}`, mark.view)}
          </div>
        </div>
        {!locked && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              disabled={index === 0}
              onClick={(e) => { e.stopPropagation(); onMoveUp(mark.id); }}
              className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
              aria-label={t("bodymap.moveUp", "Move up")}
              data-testid={`${testid}-row-up-${mark.number}`}
            ><ChevronUp size={14} className="text-[color:var(--fl-orange)]" /></button>
            <button
              type="button"
              disabled={index === total - 1}
              onClick={(e) => { e.stopPropagation(); onMoveDown(mark.id); }}
              className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
              aria-label={t("bodymap.moveDown", "Move down")}
              data-testid={`${testid}-row-down-${mark.number}`}
            ><ChevronDown size={14} className="text-[color:var(--fl-orange)]" /></button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(mark.id); }}
              className="p-1 rounded hover:bg-red-500/10"
              aria-label={t("bodymap.remove", "Remove")}
              data-testid={`${testid}-row-remove-${mark.number}`}
            ><X size={14} className="text-red-400" /></button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mt-2">
        <label className="text-[10px]" style={{ color: "var(--fl-muted)" }}>
          {t("bodymap.painBefore", "Before (1–10)")}
          <input
            type="number" min={1} max={10} value={mark.pain_before ?? ""}
            onChange={(e) => onUpdate(mark.id, { pain_before: e.target.value })}
            disabled={locked}
            className="fl-input w-full text-xs px-2 py-1 rounded mt-0.5"
            data-testid={`${testid}-row-before-${mark.number}`}
          />
        </label>
        <label className="text-[10px]" style={{ color: "var(--fl-muted)" }}>
          {t("bodymap.painAfter", "After (1–10)")}
          <input
            type="number" min={1} max={10} value={mark.pain_after ?? ""}
            onChange={(e) => onUpdate(mark.id, { pain_after: e.target.value })}
            disabled={locked}
            className="fl-input w-full text-xs px-2 py-1 rounded mt-0.5"
            data-testid={`${testid}-row-after-${mark.number}`}
          />
        </label>
        <label className="text-[10px]" style={{ color: "var(--fl-muted)" }}>
          {t("bodymap.rpe", "RPE (1–10)")}
          <input
            type="number" min={1} max={10} value={mark.rpe ?? ""}
            onChange={(e) => onUpdate(mark.id, { rpe: e.target.value })}
            disabled={locked}
            className="fl-input w-full text-xs px-2 py-1 rounded mt-0.5"
            data-testid={`${testid}-row-rpe-${mark.number}`}
          />
        </label>
      </div>
      <label className="text-[10px] block mt-2" style={{ color: "var(--fl-muted)" }}>
        {t("bodymap.description", "Description")}
        <textarea
          rows={2} value={mark.description ?? ""}
          onChange={(e) => onUpdate(mark.id, { description: e.target.value })}
          disabled={locked}
          className="fl-input w-full text-xs px-2 py-1 rounded mt-0.5 resize-y"
          placeholder={t("bodymap.descPlaceholder", "sharp / dull, when it started, what makes it worse…")}
          data-testid={`${testid}-row-desc-${mark.number}`}
        />
      </label>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function BodyMap({ value, onChange, locked, testid = "body-map" }) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(null);
  const marks = value || [];

  const addMark = (pt) => {
    if (locked) return;
    const id = crypto.randomUUID();
    const mark = {
      id,
      view: pt.view,
      x_pct: Math.round(pt.x_pct * 10000) / 10000,
      y_pct: Math.round(pt.y_pct * 10000) / 10000,
      pain_before: "",
      pain_after: "",
      rpe: "",
      description: "",
    };
    onChange([...marks, mark]);
    setActiveId(id);
  };

  const updateMark = (id, patch) =>
    onChange(marks.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  const removeMark = (id) => {
    onChange(marks.filter((m) => m.id !== id));
    if (activeId === id) setActiveId(null);
  };

  const moveMark = (id, delta) => {
    const idx = marks.findIndex((m) => m.id === id);
    const target = idx + delta;
    if (idx === -1 || target < 0 || target >= marks.length) return;
    const next = [...marks];
    const [item] = next.splice(idx, 1);
    next.splice(target, 0, item);
    onChange(next);
  };

  // Attach the global number (index+1) each render so the marker on the
  // image and its sidebar row always share the same digit.
  const numbered = marks.map((m, i) => ({ ...m, number: i + 1 }));

  return (
    <div data-testid={testid}>
      <div
        className="grid grid-cols-1 gap-4 items-start lg:grid-cols-[30%_1fr]"
      >
      {/* LEFT — 30% width. Skeleton image is the coordinate system. */}
      <div className="min-w-0" data-testid={`${testid}-map-col`}>
        <div className="fl-card p-3" style={{ border: CARD_BORDER, boxShadow: CARD_SHADOW }}>
          <BodyCanvas
            marks={numbered}
            activeId={activeId}
            onOpen={setActiveId}
            onAdd={addMark}
            locked={locked}
            testid={testid}
          />
        </div>
        <div className="text-[11px] mt-2" style={{ color: "var(--fl-muted-2)" }}>
          {locked
            ? t("assess.bodyMapLocked", "Numbered markers reflect the patient's priority order.")
            : t("assess.bodyMapClickAny", "Tap anywhere on the skeleton to drop a numbered marker. Scroll to zoom into the cursor; double-click to reset.")}
        </div>
      </div>

      {/* RIGHT — priority list. min-w-0 lets the grid actually shrink this
          column below its content width on narrow screens. */}
      <div
        className="flex flex-col min-w-0 min-h-0 fl-card p-3"
        style={{ border: CARD_BORDER, boxShadow: CARD_SHADOW }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="fl-heading text-sm flex items-center gap-2">
            <GripVertical size={14} style={{ color: FL_ORANGE }} />
            {t("bodymap.priorityList", "Priority list")}
            <span className="text-[10px] font-normal normal-case tracking-normal"
                   style={{ color: "var(--fl-muted-2)" }}
                   data-testid={`${testid}-count`}>
              · {numbered.length}{" "}
              {numbered.length === 1
                ? t("bodymap.markerSingular", "marker")
                : t("bodymap.markerPlural", "markers")}
            </span>
          </div>
        </div>
        {numbered.length === 0 ? (
          <div
            className="text-xs italic p-4 rounded-md"
            style={{ color: "var(--fl-muted-2)", border: "1px dashed var(--fl-border)" }}
            data-testid={`${testid}-empty`}
          >
            {locked
              ? t("bodymap.emptyReadonly", "No markers recorded yet.")
              : t("bodymap.empty", "No markers yet. Tap the skeleton on the left to add one.")}
          </div>
        ) : (
          <div className="space-y-2 overflow-y-auto pr-1" style={{ maxHeight: "560px" }} data-testid={`${testid}-list`}>
            {numbered.map((m, i) => (
              <MarkerRow
                key={m.id}
                mark={m}
                index={i}
                total={numbered.length}
                active={activeId === m.id}
                locked={locked}
                onFocus={setActiveId}
                onUpdate={updateMark}
                onRemove={removeMark}
                onMoveUp={(id) => moveMark(id, -1)}
                onMoveDown={(id) => moveMark(id, +1)}
                testid={testid}
              />
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
